import { makeAutoObservable, runInAction } from "mobx";

interface AddInventoryForm {
    productId: number | null;
    lotNumber: string;
    quantity: number;
    currentLocationId: number | null;
    qcStatus: string;
    notes: string;
}

interface EditInventoryForm {
    productId: number | null;
    lotNumber: string;
    quantity: number;
    currentLocationId: number | null;
    qcStatus: string;
    notes: string;
}

interface MoveInventoryForm {
    toLocationId: number | null;
    notes: string;
}

interface QCUpdateForm {
    qcStatus: string;
    qcNotes: string;
}

interface EditLocationForm {
    name: string;
    locationType: string;
    capacity: number | null;
    isActive: boolean;
    description: string;
}

interface AddTrailerForm {
    trailerNumber: string;
    type: string;
    status: string;
    currentLocationId: number | null;
    capacity: number;
    notes: string;
}

interface EditTrailerForm {
    trailerNumber: string;
    type: string;
    status: string;
    currentLocationId: number | null;
    capacity: number;
    notes: string;
}

interface LoadTrailerForm {
    trailerId: number | null;
    inventoryItems: number[];
    notes: string;
}

interface UnloadTrailerForm {
    trailerId: number | null;
    locationId: number | null;
    notes: string;
}

class YardDialogsStore {
    // Form data states
    addInventoryForm: AddInventoryForm = {
        productId: null,
        lotNumber: "",
        quantity: 1,
        currentLocationId: null,
        qcStatus: "pending",
        notes: "",
    };

    editInventoryForm: EditInventoryForm = {
        productId: null,
        lotNumber: "",
        quantity: 1,
        currentLocationId: null,
        qcStatus: "pending",
        notes: "",
    };

    moveInventoryForm: MoveInventoryForm = {
        toLocationId: null,
        notes: "",
    };

    qcUpdateForm: QCUpdateForm = {
        qcStatus: "pending",
        qcNotes: "",
    };

    editLocationForm: EditLocationForm = {
        name: "",
        locationType: "yard",
        capacity: null,
        isActive: true,
        description: "",
    };

    addTrailerForm: AddTrailerForm = {
        trailerNumber: "",
        type: "dry_van",
        status: "empty",
        currentLocationId: null,
        capacity: 0,
        notes: "",
    };

    editTrailerForm: EditTrailerForm = {
        trailerNumber: "",
        type: "dry_van",
        status: "empty",
        currentLocationId: null,
        capacity: 0,
        notes: "",
    };

    loadTrailerForm: LoadTrailerForm = {
        trailerId: null,
        inventoryItems: [],
        notes: "",
    };

    unloadTrailerForm: UnloadTrailerForm = {
        trailerId: null,
        locationId: null,
        notes: "",
    };

    // Validation errors
    addInventoryErrors: Record<string, string> = {};
    editInventoryErrors: Record<string, string> = {};
    moveInventoryErrors: Record<string, string> = {};
    qcUpdateErrors: Record<string, string> = {};
    editLocationErrors: Record<string, string> = {};
    addTrailerErrors: Record<string, string> = {};
    editTrailerErrors: Record<string, string> = {};
    loadTrailerErrors: Record<string, string> = {};
    unloadTrailerErrors: Record<string, string> = {};

    // Loading states
    isSubmitting = false;

    constructor(private rootStore: any) {
        makeAutoObservable(this, { rootStore: false } as any);
    }

    // Add Inventory Actions
    setAddInventoryField(field: keyof AddInventoryForm, value: any) {
        (this.addInventoryForm as any)[field] = value;
        if (this.addInventoryErrors[field]) {
            delete this.addInventoryErrors[field];
        }
    }

    resetAddInventoryForm() {
        this.addInventoryForm = {
            productId: null,
            lotNumber: "",
            quantity: 1,
            currentLocationId: null,
            qcStatus: "pending",
            notes: "",
        };
        this.addInventoryErrors = {};
    }

    validateAddInventory(): boolean {
        const errors: Record<string, string> = {};
        if (!this.addInventoryForm.productId) errors.productId = "Product is required";
        if (!this.addInventoryForm.lotNumber) errors.lotNumber = "Lot number is required";
        if (!this.addInventoryForm.quantity || this.addInventoryForm.quantity <= 0) {
            errors.quantity = "Quantity must be a positive number";
        }
        if (!this.addInventoryForm.currentLocationId) errors.currentLocationId = "Location is required";

        this.addInventoryErrors = errors;
        return Object.keys(errors).length === 0;
    }

    async submitAddInventory(): Promise<boolean> {
        if (!this.validateAddInventory()) return false;

        this.isSubmitting = true;
        try {
            await (this.rootStore as any).yardInventoryItemsStore.create(this.addInventoryForm);
            await (this.rootStore as any).yardInventoryItemsStore.find({ query: { $limit: 200 } });
            this.resetAddInventoryForm();
            return true;
        } catch (error) {
            console.error("Failed to add inventory:", error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }

    // Edit Inventory Actions
    setEditInventoryField(field: keyof EditInventoryForm, value: any) {
        (this.editInventoryForm as any)[field] = value;
        if (this.editInventoryErrors[field]) {
            delete this.editInventoryErrors[field];
        }
    }

    loadEditInventoryData(itemId: number) {
        const item = (this.rootStore as any).yardInventoryItemsStore?.entities.find((i: any) => i.id === itemId);
        if (item) {
            this.editInventoryForm = {
                productId: item.productId || null,
                lotNumber: item.lotNumber || "",
                quantity: item.quantity || 1,
                currentLocationId: item.currentLocationId || null,
                qcStatus: item.qcStatus || "pending",
                notes: item.notes || "",
            };
        }
    }

    validateEditInventory(): boolean {
        const errors: Record<string, string> = {};
        if (!this.editInventoryForm.productId) errors.productId = "Product is required";
        if (!this.editInventoryForm.lotNumber) errors.lotNumber = "Lot number is required";
        if (!this.editInventoryForm.quantity || this.editInventoryForm.quantity <= 0) {
            errors.quantity = "Quantity must be a positive number";
        }
        if (!this.editInventoryForm.currentLocationId) errors.currentLocationId = "Location is required";

        this.editInventoryErrors = errors;
        return Object.keys(errors).length === 0;
    }

    async submitEditInventory(itemId: number): Promise<boolean> {
        if (!this.validateEditInventory()) return false;

        this.isSubmitting = true;
        try {
            await (this.rootStore as any).yardInventoryItemsStore.patch(itemId, this.editInventoryForm);
            await (this.rootStore as any).yardInventoryItemsStore.find({ query: { $limit: 200 } });
            return true;
        } catch (error) {
            console.error("Failed to update inventory:", error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }

    // Move Inventory Actions
    setMoveInventoryField(field: keyof MoveInventoryForm, value: any) {
        (this.moveInventoryForm as any)[field] = value;
        if (this.moveInventoryErrors[field]) {
            delete this.moveInventoryErrors[field];
        }
    }

    resetMoveInventoryForm() {
        this.moveInventoryForm = {
            toLocationId: null,
            notes: "",
        };
        this.moveInventoryErrors = {};
    }

    validateMoveInventory(): boolean {
        const errors: Record<string, string> = {};
        if (!this.moveInventoryForm.toLocationId) errors.toLocationId = "Target location is required";

        this.moveInventoryErrors = errors;
        return Object.keys(errors).length === 0;
    }

    async submitMoveInventory(itemIds: number[]): Promise<boolean> {
        if (!this.validateMoveInventory()) return false;

        this.isSubmitting = true;
        try {
            for (const itemId of itemIds) {
                const item = (this.rootStore as any).yardInventoryItemsStore?.entities.find((i: any) => i.id === itemId);
                if (!item) continue;

                await (this.rootStore as any).yardInventoryItemsStore.patch(itemId, {
                    currentLocationId: this.moveInventoryForm.toLocationId,
                });

                await (this.rootStore as any).yardMovementLogsStore.create({
                    inventoryItemId: itemId,
                    fromLocationId: item.currentLocationId,
                    toLocationId: this.moveInventoryForm.toLocationId,
                    movedByUserId: (this.rootStore as any).userStore?.currentUser?.id || 1,
                    quantityMoved: item.quantity,
                    notes: this.moveInventoryForm.notes || "Moved via dialog",
                });
            }

            await (this.rootStore as any).yardInventoryItemsStore.find({ query: { $limit: 200 } });
            this.resetMoveInventoryForm();
            return true;
        } catch (error) {
            console.error("Failed to move items:", error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }

    // QC Update Actions
    setQCUpdateField(field: keyof QCUpdateForm, value: any) {
        (this.qcUpdateForm as any)[field] = value;
        if (this.qcUpdateErrors[field]) {
            delete this.qcUpdateErrors[field];
        }
    }

    resetQCUpdateForm() {
        this.qcUpdateForm = {
            qcStatus: "pending",
            qcNotes: "",
        };
        this.qcUpdateErrors = {};
    }

    validateQCUpdate(): boolean {
        const errors: Record<string, string> = {};
        if (!this.qcUpdateForm.qcStatus) errors.qcStatus = "QC status is required";
        if ((this.qcUpdateForm.qcStatus === "failed" || this.qcUpdateForm.qcStatus === "rework") && !this.qcUpdateForm.qcNotes) {
            errors.qcNotes = "Notes are required when QC fails or needs rework";
        }

        this.qcUpdateErrors = errors;
        return Object.keys(errors).length === 0;
    }

    async submitQCUpdate(itemIds: number[]): Promise<boolean> {
        if (!this.validateQCUpdate()) return false;

        this.isSubmitting = true;
        try {
            for (const itemId of itemIds) {
                await (this.rootStore as any).yardInventoryItemsStore.patch(itemId, {
                    qcStatus: this.qcUpdateForm.qcStatus,
                    qcNotes: this.qcUpdateForm.qcNotes,
                    qcDate: new Date().toISOString(),
                });
            }

            await (this.rootStore as any).yardInventoryItemsStore.find({ query: { $limit: 200 } });
            this.resetQCUpdateForm();
            return true;
        } catch (error) {
            console.error("Failed to update QC status:", error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }

    // Edit Location Actions
    setEditLocationField(field: keyof EditLocationForm, value: any) {
        (this.editLocationForm as any)[field] = value;
        if (this.editLocationErrors[field]) {
            delete this.editLocationErrors[field];
        }
    }

    loadEditLocationData(locationId: number) {
        const location = (this.rootStore as any).yardLocationsStore?.entities.find((l: any) => l.id === locationId);
        if (location) {
            this.editLocationForm = {
                name: location.name || "",
                locationType: location.locationType || "yard",
                capacity: location.capacity || null,
                isActive: location.isActive !== false,
                description: location.description || "",
            };
        }
    }

    validateEditLocation(): boolean {
        const errors: Record<string, string> = {};
        if (!this.editLocationForm.name) errors.name = "Location name is required";
        if (!this.editLocationForm.locationType) errors.locationType = "Location type is required";

        this.editLocationErrors = errors;
        return Object.keys(errors).length === 0;
    }

    async submitEditLocation(locationId: number): Promise<boolean> {
        if (!this.validateEditLocation()) return false;

        this.isSubmitting = true;
        try {
            await (this.rootStore as any).yardLocationsStore.patch(locationId, this.editLocationForm);
            await (this.rootStore as any).yardLocationsStore.find({ query: { $limit: 200 } });
            return true;
        } catch (error) {
            console.error("Failed to update location:", error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }

    // Add Trailer Actions
    setAddTrailerField(field: keyof AddTrailerForm, value: any) {
        (this.addTrailerForm as any)[field] = value;
        if (this.addTrailerErrors[field]) {
            delete this.addTrailerErrors[field];
        }
    }

    resetAddTrailerForm() {
        this.addTrailerForm = {
            trailerNumber: "",
            type: "dry_van",
            status: "empty",
            currentLocationId: null,
            capacity: 0,
            notes: "",
        };
        this.addTrailerErrors = {};
    }

    validateAddTrailer(): boolean {
        const errors: Record<string, string> = {};
        if (!this.addTrailerForm.trailerNumber) errors.trailerNumber = "Trailer number is required";
        if (!this.addTrailerForm.type) errors.type = "Trailer type is required";
        if (!this.addTrailerForm.status) errors.status = "Status is required";
        if (this.addTrailerForm.capacity < 0) errors.capacity = "Capacity must be non-negative";

        this.addTrailerErrors = errors;
        return Object.keys(errors).length === 0;
    }

    async submitAddTrailer(): Promise<boolean> {
        if (!this.validateAddTrailer()) return false;

        this.isSubmitting = true;
        try {
            await (this.rootStore as any).yardTrailersStore.create(this.addTrailerForm);
            await (this.rootStore as any).yardTrailersStore.find({ query: { $limit: 200 } });
            this.resetAddTrailerForm();
            return true;
        } catch (error) {
            console.error("Failed to add trailer:", error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }

    // Edit Trailer Actions
    setEditTrailerField(field: keyof EditTrailerForm, value: any) {
        (this.editTrailerForm as any)[field] = value;
        if (this.editTrailerErrors[field]) {
            delete this.editTrailerErrors[field];
        }
    }

    loadEditTrailerData(trailerId: number) {
        const trailer = (this.rootStore as any).yardTrailersStore?.entities.find((t: any) => t.id === trailerId);
        if (trailer) {
            this.editTrailerForm = {
                trailerNumber: trailer.trailerNumber || "",
                type: trailer.type || "dry_van",
                status: trailer.status || "empty",
                currentLocationId: trailer.currentLocationId || null,
                capacity: trailer.capacity || 0,
                notes: trailer.notes || "",
            };
        }
    }

    validateEditTrailer(): boolean {
        const errors: Record<string, string> = {};
        if (!this.editTrailerForm.trailerNumber) errors.trailerNumber = "Trailer number is required";
        if (!this.editTrailerForm.type) errors.type = "Trailer type is required";
        if (!this.editTrailerForm.status) errors.status = "Status is required";
        if (this.editTrailerForm.capacity < 0) errors.capacity = "Capacity must be non-negative";

        this.editTrailerErrors = errors;
        return Object.keys(errors).length === 0;
    }

    async submitEditTrailer(trailerId: number): Promise<boolean> {
        if (!this.validateEditTrailer()) return false;

        this.isSubmitting = true;
        try {
            await (this.rootStore as any).yardTrailersStore.patch(trailerId, this.editTrailerForm);
            await (this.rootStore as any).yardTrailersStore.find({ query: { $limit: 200 } });
            return true;
        } catch (error) {
            console.error("Failed to update trailer:", error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }

    // Load Trailer Actions
    setLoadTrailerField(field: keyof LoadTrailerForm, value: any) {
        (this.loadTrailerForm as any)[field] = value;
        if (this.loadTrailerErrors[field]) {
            delete this.loadTrailerErrors[field];
        }
    }

    resetLoadTrailerForm() {
        this.loadTrailerForm = {
            trailerId: null,
            inventoryItems: [],
            notes: "",
        };
        this.loadTrailerErrors = {};
    }

    validateLoadTrailer(): boolean {
        const errors: Record<string, string> = {};
        if (!this.loadTrailerForm.trailerId) errors.trailerId = "Trailer is required";
        if (this.loadTrailerForm.inventoryItems.length === 0) {
            errors.inventoryItems = "At least one inventory item must be selected";
        }

        this.loadTrailerErrors = errors;
        return Object.keys(errors).length === 0;
    }

    async submitLoadTrailer(): Promise<boolean> {
        if (!this.validateLoadTrailer()) return false;

        this.isSubmitting = true;
        try {
            // Update trailer status
            await (this.rootStore as any).yardTrailersStore.patch(this.loadTrailerForm.trailerId, {
                status: "loaded",
                loadedAt: new Date().toISOString(),
            });

            // Update inventory items to reference the trailer
            for (const itemId of this.loadTrailerForm.inventoryItems) {
                await (this.rootStore as any).yardInventoryItemsStore.patch(itemId, {
                    trailerId: this.loadTrailerForm.trailerId,
                    currentLocationId: null, // Remove from yard location
                });

                // Create movement log
                const item = (this.rootStore as any).yardInventoryItemsStore?.entities.find((i: any) => i.id === itemId);
                if (item) {
                    await (this.rootStore as any).yardMovementLogsStore.create({
                        inventoryItemId: itemId,
                        fromLocationId: item.currentLocationId,
                        toLocationId: null,
                        trailerId: this.loadTrailerForm.trailerId,
                        movedByUserId: (this.rootStore as any).userStore?.currentUser?.id || 1,
                        quantityMoved: item.quantity,
                        notes: this.loadTrailerForm.notes || `Loaded onto trailer`,
                    });
                }
            }

            await (this.rootStore as any).yardTrailersStore.find({ query: { $limit: 200 } });
            await (this.rootStore as any).yardInventoryItemsStore.find({ query: { $limit: 200 } });
            this.resetLoadTrailerForm();
            return true;
        } catch (error) {
            console.error("Failed to load trailer:", error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }

    // Unload Trailer Actions
    setUnloadTrailerField(field: keyof UnloadTrailerForm, value: any) {
        (this.unloadTrailerForm as any)[field] = value;
        if (this.unloadTrailerErrors[field]) {
            delete this.unloadTrailerErrors[field];
        }
    }

    resetUnloadTrailerForm() {
        this.unloadTrailerForm = {
            trailerId: null,
            locationId: null,
            notes: "",
        };
        this.unloadTrailerErrors = {};
    }

    validateUnloadTrailer(): boolean {
        const errors: Record<string, string> = {};
        if (!this.unloadTrailerForm.trailerId) errors.trailerId = "Trailer is required";
        if (!this.unloadTrailerForm.locationId) errors.locationId = "Unload location is required";

        this.unloadTrailerErrors = errors;
        return Object.keys(errors).length === 0;
    }

    async submitUnloadTrailer(): Promise<boolean> {
        if (!this.validateUnloadTrailer()) return false;

        this.isSubmitting = true;
        try {
            // Find all items in the trailer
            const itemsInTrailer = (this.rootStore as any).yardInventoryItemsStore?.entities.filter(
                (item: any) => item.trailerId === this.unloadTrailerForm.trailerId
            ) || [];

            // Move items to the specified location
            for (const item of itemsInTrailer) {
                await (this.rootStore as any).yardInventoryItemsStore.patch(item.id, {
                    trailerId: null,
                    currentLocationId: this.unloadTrailerForm.locationId,
                });

                // Create movement log
                await (this.rootStore as any).yardMovementLogsStore.create({
                    inventoryItemId: item.id,
                    fromLocationId: null,
                    toLocationId: this.unloadTrailerForm.locationId,
                    trailerId: this.unloadTrailerForm.trailerId,
                    movedByUserId: (this.rootStore as any).userStore?.currentUser?.id || 1,
                    quantityMoved: item.quantity,
                    notes: this.unloadTrailerForm.notes || `Unloaded from trailer`,
                });
            }

            // Update trailer status
            await (this.rootStore as any).yardTrailersStore.patch(this.unloadTrailerForm.trailerId, {
                status: "empty",
                unloadedAt: new Date().toISOString(),
            });

            await (this.rootStore as any).yardTrailersStore.find({ query: { $limit: 200 } });
            await (this.rootStore as any).yardInventoryItemsStore.find({ query: { $limit: 200 } });
            this.resetUnloadTrailerForm();
            return true;
        } catch (error) {
            console.error("Failed to unload trailer:", error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }
}

export default YardDialogsStore;